import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PhoneIcon from '@mui/icons-material/Phone';
import NoteIcon from '@mui/icons-material/Note';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ActivityEntry, ActivityEntryType } from '~/types/coordinate';

interface Props {
  patientId: string;
  entries: ActivityEntry[];
  onAddEntry: (entry: Omit<ActivityEntry, 'id'>) => void;
}

const TYPE_META: Record<ActivityEntryType, { label: string; icon: React.ReactNode; color: string }> = {
  call:          { label: 'Call',          icon: <PhoneIcon sx={{ fontSize: 14 }} />,        color: '#1565c0' },
  note:          { label: 'Note',          icon: <NoteIcon sx={{ fontSize: 14 }} />,          color: '#635f99' },
  status_change: { label: 'Status',        icon: <SwapHorizIcon sx={{ fontSize: 14 }} />,     color: '#616161' },
  document:      { label: 'Document',      icon: <AttachFileIcon sx={{ fontSize: 14 }} />,    color: '#006e60' },
  task:          { label: 'Task',          icon: <TaskAltIcon sx={{ fontSize: 14 }} />,        color: '#e65100' },
  agent:         { label: 'Coordinate AI', icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} />,  color: '#635f99' },
};

const LOGGABLE_TYPES: ActivityEntryType[] = ['call', 'note', 'document', 'task'];

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const EntryRow: React.FC<{ entry: ActivityEntry }> = ({ entry }) => {
  const meta = TYPE_META[entry.type];
  const isAgent = entry.isAgent;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.25,
        px: isAgent ? 1 : 0,
        borderRadius: isAgent ? 1 : 0,
        borderLeft: isAgent ? '2px solid rgba(99,95,153,0.4)' : '2px solid transparent',
      }}
    >
      <Avatar
        sx={{
          width: 26, height: 26, mt: 0.2,
          bgcolor: isAgent ? '#f0eef8' : '#f5f5f5',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(meta.icon as React.ReactElement, {
          sx: { fontSize: 13, color: isAgent ? '#635f99' : meta.color },
        })}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              height: 17, fontSize: '0.62rem', fontWeight: 700,
              bgcolor: isAgent ? '#f0eef8' : '#f5f5f5',
              color: isAgent ? '#635f99' : meta.color,
            }}
          />
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
            {formatTimestamp(entry.timestamp)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: '0.7rem', fontWeight: isAgent ? 600 : 400, color: isAgent ? '#635f99' : 'text.secondary' }}
          >
            {entry.author}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.4, fontSize: '0.82rem' }}>
          {entry.content}
        </Typography>
      </Box>
    </Box>
  );
};

const LogActivityDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (entry: Omit<ActivityEntry, 'id'>) => void;
}> = ({ open, onClose, onSave }) => {
  const [type, setType] = useState<ActivityEntryType>('call');
  const [content, setContent] = useState('');

  const handleSave = () => {
    if (!content.trim()) return;
    onSave({
      type,
      timestamp: new Date().toISOString(),
      author: 'You',
      content: content.trim(),
      isAgent: false,
    });
    setContent('');
    setType('call');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log Activity</DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
          Type
        </Typography>
        <Select
          fullWidth
          size="small"
          value={type}
          onChange={e => setType(e.target.value as ActivityEntryType)}
          sx={{ mb: 2.5 }}
        >
          {LOGGABLE_TYPES.map(t => (
            <MenuItem key={t} value={t}>{TYPE_META[t].label}</MenuItem>
          ))}
        </Select>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notes"
          placeholder={type === 'call' ? 'Who did you call, what was discussed, what happens next?' : 'Details…'}
          value={content}
          onChange={e => setContent(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!content.trim()}>
          Log
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CaseActivityFeed: React.FC<Props> = ({ patientId, entries, onAddEntry }) => {
  const [logOpen, setLogOpen] = useState(false);
  const sorted = [...entries].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>Activity</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCommentIcon sx={{ fontSize: 14 }} />}
          onClick={() => setLogOpen(true)}
          sx={{ fontSize: '0.75rem' }}
        >
          Log Activity
        </Button>
      </Stack>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: 'center' }}>
          No activity logged yet
        </Typography>
      ) : (
        <Box>
          {sorted.map((entry, i) => (
            <React.Fragment key={entry.id}>
              <EntryRow entry={entry} />
              {i < sorted.length - 1 && <Divider sx={{ opacity: 0.4 }} />}
            </React.Fragment>
          ))}
        </Box>
      )}

      <LogActivityDialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSave={entry => onAddEntry(entry)}
      />
    </Box>
  );
};

export default CaseActivityFeed;
